import { Injectable, inject } from '@angular/core';
import { of, catchError, firstValueFrom } from 'rxjs';

import { ApiService } from '../../api/api.service';
import { LoggingService } from '../../logging/logging.service';
import { ChangedItemNotificationsResponse } from '../models/cache-models';

/**
 * Service for handling data API calls related to cache invalidation.
 * Pure API client - no timestamp management (handled by CacheRevalidationService).
 */
@Injectable({
  providedIn: 'root'
})
export class DataApiService {
  private readonly apiService = inject(ApiService);
  private readonly logger = inject(LoggingService);

  /**
   * Check if a specific item needs invalidation by calling the changed-since endpoint.
   *
   * @param storeName Name of the store to check.
   * @param projectId Project ID to scope the request.
   * @param itemId ID of the specific item to check.
   * @param sinceTimestamp Timestamp to check changes since.
   *
   * @returns Promise<boolean> indicating if the item needs invalidation.
   */
  public async checkItemNeedsInvalidation(
    storeName: string,
    projectId: string,
    itemId: string | number,
    sinceTimestamp: number
  ): Promise<boolean> {
    try {
      const notifications = await this.fetchChangedSinceNotifications(storeName, projectId, sinceTimestamp);

      // Check if the specific item is in the changed items list
      return notifications.items.some(item => item.id.toString() === itemId.toString());
    } catch (error) {
      this.logger.error(`Error checking if item ${itemId} needs invalidation:`, error);
      // On error, assume item doesn't need invalidation to avoid unnecessary API calls
      return false;
    }
  }

  /**
   * Get all changed item notifications for a store.
   *
   * @param storeName Name of the store to check.
   * @param projectId Project ID to scope the request.
   * @param sinceTimestamp Timestamp to check changes since.
   *
   * @returns Promise of changed item notifications.
   */
  public async fetchChangedSinceNotifications(
    storeName: string,
    projectId: string,
    sinceTimestamp: number
  ): Promise<ChangedItemNotificationsResponse> {
    // Fetch lightweight notifications (IDs only) from API
    const apiUrl = `projects/${projectId}/${storeName}/changed-since`;

    try {
      const response = await firstValueFrom(this.apiService.get<ChangedItemNotificationsResponse>(apiUrl, undefined, {
        timestamp: sinceTimestamp,
        'ngsw-bypass': true
      }).pipe(
        catchError(error => {
          this.logger.error(`Error fetching changed item notifications for ${storeName}:`, error);
          return of({ items: [], timestamp: Date.now() });
        })
      ));

      return response;
    } catch (error) {
      this.logger.error(`Error fetching changed item notifications for ${storeName}:`, error);
      return { items: [], timestamp: Date.now() };
    }
  }
}
